import { formatDollars } from '@fruitful/fruitils';
import {
    type InlineDataTableColumn,
    type InlineDataTableColumnFormat,
    type InlineDataTableRow,
} from '@fruitful/types';

export const formatInlineDataTableCell = (
    value: string | number | null,
    format: InlineDataTableColumnFormat | undefined,
): string => {
    if (value == null || value === '') {
        return '';
    }
    if (format === 'currency' && typeof value === 'number') {
        return formatDollars(value / 100, false);
    }

    if (format === 'percent' && typeof value === 'number') {
        return `${value.toString()}%`;
    }

    return String(value);
};

const isTotalsRow = (
    row: InlineDataTableRow,
    totalsRowLabel: string,
): boolean => {
    const normalizedLabel = row.label.trim().toLowerCase();
    const normalizedTotalsLabel = totalsRowLabel.trim().toLowerCase();

    return (
        row.isTotalsRow === true ||
        normalizedLabel === normalizedTotalsLabel ||
        normalizedLabel === 'totals'
    );
};

const sumNumericCells = (
    rows: InlineDataTableRow[],
    columnKey: string,
): number =>
    rows.reduce((sum, row) => {
        const value = row.cells[columnKey];
        return typeof value === 'number' ? sum + value : sum;
    }, 0);

export const withAutoTotalsRow = (
    rows: InlineDataTableRow[],
    columns: InlineDataTableColumn[],
    totalsRowLabel: string | undefined,
): InlineDataTableRow[] => {
    if (totalsRowLabel == null || totalsRowLabel.trim() === '') {
        return rows;
    }

    const dataRows = rows.filter((row) => !isTotalsRow(row, totalsRowLabel));

    if (
        dataRows.length <= 1 ||
        rows.some((row) => isTotalsRow(row, totalsRowLabel))
    ) {
        return rows;
    }

    const totalsCells: Record<string, string | number> = {};

    for (const column of columns) {
        if (column.format === 'currency') {
            totalsCells[column.key] = sumNumericCells(dataRows, column.key);
            continue;
        }

        totalsCells[column.key] = '';
    }

    return [
        ...dataRows,
        {
            label: totalsRowLabel,
            cells: totalsCells,
            isTotalsRow: true,
        },
    ];
};
